/**
 * Full-screen, non-dismissible "update required" gate. Rendered above the app
 * when the installed version is below the server-configured minimum. There is no
 * "Later" — the only action is to open the store.
 */

import { View, Text, Pressable, Platform } from 'react-native';
import { ArrowUpCircle } from 'lucide-react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../constants/animations';
import { theme } from '../../../../theme';
import { openAppStore } from '../../helpers/openAppStore.helper';
import type { AppPlatform } from '../../helpers/appVersionApi.helper';
import { updateGateStyles as styles } from './UpdateGate.styles';

const DEFAULT_TITLE = 'Update required';
const DEFAULT_MESSAGE =
  'A new version of Zporti is available. Please update to keep playing.';

interface UpdateGateProps {
  platform: AppPlatform;
  message: string | null;
}

export function UpdateGate({ platform, message }: UpdateGateProps) {
  return (
    <View style={styles.overlay}>
      <View style={styles.content}>
        <View style={styles.iconWrap}>
          <ArrowUpCircle size={44} color={theme.primary} />
        </View>
        <Text style={styles.title}>{DEFAULT_TITLE}</Text>
        <Text style={styles.message}>{message ?? DEFAULT_MESSAGE}</Text>
        <Pressable
          accessibilityRole="button"
          onPress={() => {
            void openAppStore(platform);
          }}
          style={({ pressed, hovered }) => [
            styles.button,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.buttonPressed,
            hovered && Platform.OS === 'web' && styles.buttonHovered,
          ]}
        >
          <Text style={styles.buttonText}>Update Now</Text>
        </Pressable>
      </View>
    </View>
  );
}
